/**
 * Wer ist gerade im Portal — kompakte Anzeige in der Kopfleiste.
 *
 * Datenquelle ist `GET /api/praesenz`. Der Zustand liegt im Backend im
 * Arbeitsspeicher, nicht in der Datenbank: "online" ist eine Momentaufnahme,
 * die niemand rückwirkend braucht.
 *
 * Zwei bewusste Entscheidungen:
 *
 *  1. Die ANZAHL sieht jedes Mitglied, die NAMEN nur der Vorstand. Dass ein
 *     Verein zeigt, wie viel gerade los ist, ist harmlos; dass jedes Mitglied
 *     sieht, welches andere gerade am Rechner sitzt, ist es nicht. Ob die
 *     Namensliste kommt, entscheidet das Backend — hier wird nur angezeigt,
 *     was geliefert wurde.
 *
 *  2. Nachgefragt wird nur, solange der Tab sichtbar ist. Das hält nicht nur
 *     die Last klein, es macht die Anzeige ehrlicher: ein vergessener
 *     Hintergrund-Tab hört auf zu melden und fällt von selbst aus der Liste,
 *     statt jemanden stundenlang als anwesend auszugeben.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

interface Person {
  id: number;
  name: string;
  vorname: string | null;
  nachname: string | null;
  primaer_rolle: string | null;
  rollen: string[];
  seit_minuten: number;
  untaetig_minuten: number;
  ich: boolean;
}

interface Praesenz {
  anzahl: number;
  fenster_minuten: number;
  personen: Person[] | null;
}

/** Alle 60 Sekunden — oft genug, um zu stimmen, selten genug, um nicht zu stören. */
const TAKT_MS = 60_000;

const ROLLE_TEXT: Record<string, string> = {
  board: 'Vorstand',
  trainer: 'Trainer',
  member: 'Mitglied',
  guest: 'Gast',
  auditor: 'Kassenprüfer',
};

const initialen = (p: Person) => {
  const a = (p.vorname || p.name || '').trim()[0] || '';
  const b = (p.nachname || '').trim()[0] || '';
  return (a + b).toUpperCase() || '?';
};

/**
 * Rollenbezeichnung. Bewusst gegen Nicht-Strings abgesichert: das Backend
 * kennt `primary_role` intern als vollständiges Rollenobjekt, und ein Objekt
 * in JSX beendet den Renderbaum mit einer Ausnahme.
 */
const rolleText = (rolle: unknown): string => {
  const schluessel = typeof rolle === 'string' ? rolle : '';
  return ROLLE_TEXT[schluessel] || schluessel || 'Mitglied';
};

const dauer = (minuten: number) => {
  if (minuten < 1) return 'gerade eben';
  if (minuten < 60) return `seit ${minuten} Min.`;
  const std = Math.floor(minuten / 60);
  return `seit ${std} Std.`;
};

const PraesenzAnzeige: React.FC = () => {
  const [daten, setDaten] = useState<Praesenz | null>(null);
  const [offen, setOffen] = useState(false);
  const huelle = useRef<HTMLDivElement>(null);

  const holen = useCallback(async () => {
    if (document.hidden) return;
    try {
      const res = await apiClient.get('/api/praesenz');
      setDaten(res.data);
    } catch {
      // Stillschweigend: die Anzeige ist Beiwerk. Fällt sie aus, soll sie
      // verschwinden und keine Fehlermeldung über die Kopfleiste legen.
      setDaten(null);
    }
  }, []);

  useEffect(() => {
    holen();
    const takt = setInterval(holen, TAKT_MS);
    // Beim Zurückkommen sofort aktualisieren, statt bis zum nächsten Takt eine
    // veraltete Zahl zu zeigen.
    const beiSichtbar = () => {
      if (!document.hidden) holen();
    };
    document.addEventListener('visibilitychange', beiSichtbar);
    window.addEventListener('focus', beiSichtbar);
    return () => {
      clearInterval(takt);
      document.removeEventListener('visibilitychange', beiSichtbar);
      window.removeEventListener('focus', beiSichtbar);
    };
  }, [holen]);

  // Klick nach außen schließt die Liste.
  useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: MouseEvent) => {
      if (huelle.current && !huelle.current.contains(e.target as Node)) setOffen(false);
    };
    const beiEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false);
    };
    document.addEventListener('mousedown', beiKlick);
    document.addEventListener('keydown', beiEscape);
    return () => {
      document.removeEventListener('mousedown', beiKlick);
      document.removeEventListener('keydown', beiEscape);
    };
  }, [offen]);

  if (!daten || daten.anzahl < 1) return null;

  const mitNamen = !!daten.personen?.length;
  const beschriftung = `${daten.anzahl} ${daten.anzahl === 1 ? 'Person' : 'Personen'} im Portal`;

  const pille = (
    <>
      <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-success-500" />
      </span>
      <span className="text-[13px] font-bold tabular-nums text-gray-700 dark:text-gray-200">
        {daten.anzahl}
      </span>
      <span className="hidden text-[13px] text-gray-500 dark:text-gray-400 sm:inline">online</span>
    </>
  );

  const rahmen =
    'flex items-center gap-1.5 rounded-xl border border-gray-200 px-2.5 py-1.5 dark:border-gray-700';

  if (!mitNamen) {
    return (
      <div
        className={rahmen}
        title={`${beschriftung} — zuletzt aktiv in den letzten ${daten.fenster_minuten} Minuten`}
      >
        {pille}
      </div>
    );
  }

  /** Der Listeninhalt — identisch im Slide-up und im Aufklappmenü. */
  const inhalt = (
    <>
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
        <UserGroupIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Gerade im Portal
        </h3>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {daten.personen!.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5 last:border-b-0 dark:border-gray-700/60"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-[12px] font-bold text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              {initialen(p)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                {p.name}
                {p.ich && <span className="ml-1 font-normal text-gray-400">(du)</span>}
              </p>
              <p className="truncate text-[11px] text-gray-400">
                {rolleText(p.primaer_rolle)} · {dauer(p.seit_minuten)}
              </p>
            </div>
            {p.untaetig_minuten >= 2 && (
              <span
                className="shrink-0 text-[11px] text-gray-400"
                title={`Letzter Aufruf vor ${p.untaetig_minuten} Minuten`}
              >
                {p.untaetig_minuten} Min. still
              </span>
            )}
          </div>
        ))}
      </div>

      <p className="border-t border-gray-200 px-4 py-2 text-[11px] leading-snug text-gray-400 dark:border-gray-700 dark:text-gray-500">
        Wer in den letzten {daten.fenster_minuten} Minuten etwas aufgerufen hat. Die Namen sieht nur
        der Vorstand.
      </p>
    </>
  );

  return (
    <div className="relative" ref={huelle}>
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        aria-label={beschriftung}
        title={beschriftung}
        className={`${rahmen} transition-colors hover:bg-gray-100 dark:hover:bg-gray-700`}
      >
        {pille}
      </button>

      {offen && (
        <>
          {/*
            Auf dem Handy als Slide-up über die volle Breite. Ein am Knopf
            hängendes Aufklappmenü ragte dort links aus dem Bild: der Knopf
            steht nicht am rechten Rand, und 288 px Liste passen nicht mehr
            zwischen ihn und den Bildschirmrand.

            Über ein Portal an den body, NICHT an dieser Stelle im Baum: die
            Kopfleiste trägt `backdrop-blur`, und ein backdrop-filter macht das
            Element zum Bezugsrahmen für `position: fixed`. Das Sheet klebte
            dadurch am unteren Rand des 55 px hohen Kopfes statt am unteren
            Bildschirmrand.
          */}
          {createPortal(
            <div
              className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:hidden"
              onClick={() => setOffen(false)}
            >
              <div
                className="max-h-[80vh] w-full animate-slide-up overflow-hidden rounded-t-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]"
                onClick={(e) => e.stopPropagation()}
                // Das Sheet hängt per Portal am body und liegt damit AUSSERHALB
                // von `huelle` — ohne dies würde der Klick-nach-außen-Wächter
                // jeden Klick im Sheet als "daneben" werten und sofort schließen.
                onMouseDown={(e) => e.stopPropagation()}
              >
                {inhalt}
              </div>
            </div>,
            document.body
          )}

          {/* Ab sm als Aufklappmenü am Knopf */}
          <div className="absolute right-0 z-40 mt-2 hidden w-72 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg sm:block dark:border-gray-700 dark:bg-[#1c2432]">
            {inhalt}
          </div>
        </>
      )}
    </div>
  );
};

export default PraesenzAnzeige;
